import { z } from "zod";

export const commerceOrderStatusSchema = z.enum([
  "created",
  "paid",
  "completed",
  "refunded",
  "canceled",
]);
export type CommerceOrderStatus = z.infer<typeof commerceOrderStatusSchema>;

export const commerceOrderTransitions: Record<CommerceOrderStatus, readonly CommerceOrderStatus[]> = {
  created: ["paid", "canceled"],
  paid: ["completed", "refunded"],
  completed: ["refunded"],
  refunded: [],
  canceled: [],
};

export function canTransitionCommerceOrder(from: CommerceOrderStatus, to: CommerceOrderStatus): boolean {
  return commerceOrderTransitions[from].includes(to);
}

const PAYLOAD_MAX_BYTES = 16_384;

export function commerceOrderPayloadBytes(value: Record<string, unknown>): number {
  return new TextEncoder().encode(JSON.stringify(value)).length;
}

export const commerceOrderPayloadSchema = z.record(z.string(), z.unknown()).superRefine((value, context) => {
  if (commerceOrderPayloadBytes(value) > PAYLOAD_MAX_BYTES) {
    context.addIssue({ code: "custom", message: "业务数据不能超过 16KB" });
  }
});

const commerceOrderIdSchema = z.string().trim().min(8).max(128);
const timestamp = z.string().datetime();

export const createCommerceOrderInputSchema = z.object({
  idempotencyKey: z.string().trim().min(8).max(128),
  amountMinor: z.number().int().min(0).max(1_000_000_000),
  currency: z.string().trim().regex(/^[A-Z]{3}$/).default("CNY"),
  title: z.string().trim().min(1).max(128),
  titleUg: z.string().trim().max(128).optional(),
  payload: commerceOrderPayloadSchema.default({}),
}).strict();
export type CreateCommerceOrderInput = z.infer<typeof createCommerceOrderInputSchema>;

export const transitionCommerceOrderInputSchema = z.object({
  status: z.enum(["paid", "completed", "refunded", "canceled"]),
  expectedVersion: z.number().int().positive(),
}).strict();
export type TransitionCommerceOrderInput = z.infer<typeof transitionCommerceOrderInputSchema>;

export const settleExternalCommerceOrderInputSchema = z.object({
  expectedVersion: z.number().int().positive(),
}).strict();
export type SettleExternalCommerceOrderInput = z.infer<typeof settleExternalCommerceOrderInputSchema>;

export const updateCommerceOrderTitleUgInputSchema = z.object({
  titleUg: z.string().trim().max(128),
  expectedVersion: z.number().int().positive(),
}).strict();
export type UpdateCommerceOrderTitleUgInput = z.infer<typeof updateCommerceOrderTitleUgInputSchema>;

export function isExternalCommerceSettlement(payload: Record<string, unknown>): boolean {
  return payload.settlement === "external" && payload.channel !== "wechat_virtual";
}

export const commerceOrderSchema = z.object({
  id: commerceOrderIdSchema,
  appId: z.string().min(1).max(128),
  userId: z.string().min(1).max(128),
  idempotencyKey: z.string().min(8).max(128),
  amountMinor: z.number().int().min(0).max(1_000_000_000),
  currency: z.string().regex(/^[A-Z]{3}$/),
  title: z.string().min(1).max(128),
  titleUg: z.string().max(128).optional(),
  status: commerceOrderStatusSchema,
  payload: z.record(z.string(), z.unknown()),
  paymentOrderId: z.string().max(128).nullable(),
  version: z.number().int().positive(),
  createdAt: timestamp,
  updatedAt: timestamp,
  paidAt: timestamp.nullable(),
  completedAt: timestamp.nullable(),
  refundedAt: timestamp.nullable(),
  canceledAt: timestamp.nullable(),
});
export type CommerceOrder = z.infer<typeof commerceOrderSchema>;

export const commerceOrderListItemSchema = commerceOrderSchema.omit({ payload: true });
export type CommerceOrderListItem = z.infer<typeof commerceOrderListItemSchema>;

export const commerceOrderListQuerySchema = z.object({
  status: z.union([commerceOrderStatusSchema, z.literal("all")]).default("all"),
  page: z.coerce.number().int().min(1).max(100000).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
}).strict();
export type CommerceOrderListQuery = z.infer<typeof commerceOrderListQuerySchema>;

export const commerceOrderListResultSchema = z.object({
  items: z.array(commerceOrderListItemSchema),
  total: z.number().int().nonnegative(),
});
export type CommerceOrderListResult = z.infer<typeof commerceOrderListResultSchema>;

export const commerceOrderEventSchema = z.object({
  id: z.string().min(1),
  orderId: commerceOrderIdSchema,
  type: z.enum(["status", "notification"]),
  fromStatus: commerceOrderStatusSchema.nullable(),
  toStatus: commerceOrderStatusSchema.nullable(),
  eventKey: z.string().max(64).nullable(),
  channel: z.enum(["in_app"]).nullable(),
  note: z.string().max(500),
  createdAt: timestamp,
});
export type CommerceOrderEvent = z.infer<typeof commerceOrderEventSchema>;

export const adminCommerceOrderSchema = z.object({
  id: commerceOrderIdSchema,
  appId: z.string().min(1).max(128),
  appName: z.string().min(1),
  appCode: z.string().min(1),
  userId: z.string().min(1).max(128),
  userPublicId: z.string().regex(/^\d+$/),
  userName: z.string().min(1),
  userAvatarUrl: z.union([
    z.literal("/open/user/avatar"),
    z.string().regex(/^https:\/\//).max(500),
    z.null(),
  ]).optional(),
  idempotencyKey: z.string().min(8).max(128),
  amountMinor: z.number().int().min(0).max(1_000_000_000),
  currency: z.string().regex(/^[A-Z]{3}$/),
  title: z.string().min(1).max(128),
  titleUg: z.string().max(128).optional(),
  status: commerceOrderStatusSchema,
  paymentOrderId: z.string().max(128).nullable(),
  version: z.number().int().positive(),
  createdAt: timestamp,
  updatedAt: timestamp,
  paidAt: timestamp.nullable(),
  completedAt: timestamp.nullable(),
  refundedAt: timestamp.nullable(),
  canceledAt: timestamp.nullable(),
  source: z.enum(["business_order", "wechat_virtual"]).optional(),
  sandbox: z.boolean().optional(),
  purchaseId: z.string().uuid().nullable().optional(),
  refundedAmountMinor: z.number().int().min(0).max(1_000_000_000).optional(),
  virtualDelivered: z.boolean().nullable().optional(),
});
export type AdminCommerceOrder = z.infer<typeof adminCommerceOrderSchema>;

export const adminCommerceOrderDetailSchema = z.object({
  order: adminCommerceOrderSchema.extend({ payload: z.record(z.string(), z.unknown()) }),
  events: z.array(commerceOrderEventSchema),
});
export type AdminCommerceOrderDetail = z.infer<typeof adminCommerceOrderDetailSchema>;

export const adminCommerceOrderListQuerySchema = z.object({
  keyword: z.string().trim().max(100).default(""),
  status: z.union([commerceOrderStatusSchema, z.literal("all")]).default("all"),
  appId: z.string().trim().max(128).default(""),
  dateFrom: z.union([z.literal(""), z.iso.date().refine(value => !value.startsWith("0000-"), "日期年份必须大于零")]).default(""),
  dateTo: z.union([z.literal(""), z.iso.date().refine(value => !value.startsWith("0000-"), "日期年份必须大于零")]).default(""),
  page: z.coerce.number().int().min(1).max(100000).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
}).strict().superRefine((value, context) => {
  if (value.dateFrom && value.dateTo && value.dateFrom > value.dateTo) {
    context.addIssue({ code: "custom", path: ["dateTo"], message: "结束日期不能早于开始日期" });
  }
});
export type AdminCommerceOrderListQuery = z.infer<typeof adminCommerceOrderListQuerySchema>;

export const adminCommerceOrderListResultSchema = z.object({
  items: z.array(adminCommerceOrderSchema),
  total: z.number().int().nonnegative(),
  summary: z.object({
    total: z.number().int().nonnegative(),
    created: z.number().int().nonnegative(),
    paid: z.number().int().nonnegative(),
    completed: z.number().int().nonnegative(),
    refunded: z.number().int().nonnegative(),
    canceled: z.number().int().nonnegative(),
  }),
});
export type AdminCommerceOrderListResult = z.infer<typeof adminCommerceOrderListResultSchema>;
